import React, { useEffect, useState, useRef } from 'react'
import { withStyles } from '@material-ui/core'
import { connect } from 'react-redux'

import MessageGroup from '../MessageGroup/MessageGroup'

import { searchMessages, setMessageRead } from '../../api/user'
import { saveSupportUserMessages, saveSupportUserMoreMessages, saveSupportUserNewerMessages } from '../../actions/activeUser'

import { styles } from './styles'
import LoaderSmall from 'uiKit/loaders/loaderSmall'
import { formatDate } from 'helpers/formatDate/formatDate'
import { updateUserUnreadMessages } from '../../actions/users'
import { isElementTop } from 'helpers/isElementTop'
import { isElementBottom } from 'helpers/isElementBottom'
import { AFTER_MESSAGES, BEFORE_MESSAGES } from './config'
import { ChatUserType } from 'models/ChatMessagesTypes'
import { SupportUserType } from 'models/SupportUserType'

interface Props {
  classes: any
  user: ChatUserType & SupportUserType & { filterMessageIds: string[], focusedNativeMessageId: string | null }
  activeBot: any
  searchQuery: string
  chatId: string
}

const MessagesWrap: React.FC<Props> = (props) => {
  const { classes, user, activeBot, searchQuery, chatId } = props
  const [dates, setDates] = useState([])
  const [hasNext, setHasNext] = useState(false)
  const [hasAfter, setHasAfter] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingNewer, setLoadingNewer] = useState(false)
  const scrollContainer = useRef(null)
  const messagesEnd = useRef(null)
  const messageIntoView = useRef(null)
  // Tracks whether the chatId effect's async fetch is still in progress so the
  // focusedNativeMessageId effect knows to stay out of the way.
  const isChatLoading = useRef(false)
  // Always reflects the latest focusedNativeMessageId — used inside async callbacks
  // where the closure would otherwise be stale.
  const focusedNativeMessageIdRef = useRef(user.focusedNativeMessageId)
  focusedNativeMessageIdRef.current = user.focusedNativeMessageId

  // Scroll the container to a message identified by its native message id.
  const scrollToMessage = (nativeMessageId: string) => {
    requestAnimationFrame(() => {
      const el = scrollContainer.current?.querySelector(`[data-native-id="${nativeMessageId}"]`)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    })
  }

  const scrollToBottom = () => {
    if (messagesEnd.current) {
      messagesEnd.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Returns true if the native message id is already in the currently rendered list.
  const isMessageLoaded = (targetId: string): boolean => {
    return user.messages.some(msg => {
      try {
        const nm = msg.nativeMessage ? JSON.parse(msg.nativeMessage) : null
        return nm?.message?.id === targetId
      } catch {
        return false
      }
    })
  }

  // Scroll to a filtered message. If it is not in the current window, fetch a
  // fresh window centred on it (replacing the list) then scroll.
  const jumpToMessage = (targetId: string) => {
    if (isMessageLoaded(targetId)) {
      scrollToMessage(targetId)
      return
    }

    setLoading(true)
    searchMessages(chatId, activeBot.id, targetId)
      .then((res) => {
        saveSupportUserMessages(res)
        setHasNext(res?.data?.hasBeforeFeedbackResponse ?? false)
        setHasAfter(res?.data?.hasAfterFeedbackResponse ?? false)
      })
      .then(() => {
        setLoading(false)
        scrollToMessage(targetId)
      })
      .catch(() => setLoading(false))
  }

  // Load initial messages when the active chat changes.
  useEffect(() => {
    if (chatId && activeBot?.id) {
      isChatLoading.current = true
      setLoading(true)
      setHasNext(false)
      setHasAfter(false)

      searchMessages(chatId, activeBot.id)
        .then((res) => {
          saveSupportUserMessages(res)
          setHasNext(res?.data?.hasBeforeFeedbackResponse ?? false)
          setHasAfter(res?.data?.hasAfterFeedbackResponse ?? false)
        })
        .then(() => {
          const targetId = focusedNativeMessageIdRef.current
          if (targetId) {
            // jumpToMessage handles setLoading(false) and the scroll internally.
            jumpToMessage(targetId)
          } else {
            setLoading(false)
            scrollToBottom()
          }
          isChatLoading.current = false
        })
        .catch(() => {
          setLoading(false)
          isChatLoading.current = false
        })
    }
  }, [chatId, activeBot])

  // Jump to a filtered message when the user clicks Prev / Next in ChatHeader.
  // Skipped while the chatId effect is still running (it handles the initial jump).
  useEffect(() => {
    const targetId = user.focusedNativeMessageId
    if (!targetId || isChatLoading.current) return
    jumpToMessage(targetId)
  }, [user.focusedNativeMessageId])

  useEffect(() => {
    if (user.messages?.length) {
      updateMessageDates()
      updateMessageRead()
    } else {
      setDates([])
    }
  }, [user.messages?.length, user.chatId])

  useEffect(() => {
    if (searchQuery && messageIntoView.current) {
      messageIntoView.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [searchQuery, messageIntoView.current])

  const updateMessageDates = () => {
    const dates = user.messages.map(item => item.lastEventAt?.split('T')[0])
    setDates([...new Set(dates)])
  }

  const updateMessageRead = () => {
    const lastMessage = user?.messages[0]
    if (lastMessage?.chatHistoryId && user?.chatId) {
      setMessageRead(user.chatId, activeBot.id).then(() => {
        updateUserUnreadMessages(user)
      })
    }
  }

  const getOldestMessageId = () => {
    if (!user.messages?.length) return undefined
    const oldest = user.messages[user.messages.length - 1]
    const nativeMessage = oldest?.nativeMessage ? JSON.parse(oldest.nativeMessage) : null
    return nativeMessage?.message?.id as string | undefined
  }

  const getNewestMessageId = () => {
    if (!user.messages?.length) return undefined
    const newest = user.messages[0]
    const nativeMessage = newest?.nativeMessage ? JSON.parse(newest.nativeMessage) : null
    return nativeMessage?.message?.id as string | undefined
  }

  const handleScroll = ({ target }) => {
    if (isElementTop(target) && hasNext && !loadingMore && target === scrollContainer.current && user.chatId) {
      const prevScrollHeight = scrollContainer.current.scrollHeight
      const oldestMessageId = getOldestMessageId()
      setLoadingMore(true)
      searchMessages(chatId, activeBot.id, oldestMessageId, 20, BEFORE_MESSAGES)
        .then((res) => {
          saveSupportUserMoreMessages(res)
          setHasNext(res?.data?.hasBeforeFeedbackResponse ?? false)
          requestAnimationFrame(() => {
            if (scrollContainer.current) {
              scrollContainer.current.scrollTop = scrollContainer.current.scrollHeight - prevScrollHeight
            }
          })
        })
        .catch(() => undefined)
        .finally(() => setLoadingMore(false))
    }

    if (isElementBottom(target) && hasAfter && !loadingNewer && target === scrollContainer.current && user.chatId) {
      const newestMessageId = getNewestMessageId()
      setLoadingNewer(true)
      searchMessages(chatId, activeBot.id, newestMessageId, 20, AFTER_MESSAGES)
        .then((res) => {
          saveSupportUserNewerMessages(res)
          setHasAfter(res?.data?.hasAfterFeedbackResponse ?? false)
        })
        .catch(() => undefined)
        .finally(() => setLoadingNewer(false))
    }
  }

  return (
    <>
      {loading ? (
        <div className={classes.loaderWrap}>
          <LoaderSmall showLoader={true} />
        </div>
      ) : (
        <div className={classes.container} ref={scrollContainer} onScroll={handleScroll}>
          {loadingMore && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <LoaderSmall showLoader={true} />
            </div>
          )}
          {dates
            .slice()
            .reverse()
            .map((date) => (
              <div key={date}>
                <p className={classes.date}>{formatDate(date)}</p>
                <MessageGroup
                  date={date}
                  messages={user.messages}
                  messageIntoView={messageIntoView}
                  searchQuery={searchQuery}
                />
              </div>
            ))}
          {loadingNewer && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <LoaderSmall showLoader={true} />
            </div>
          )}
          <div className={classes.bottomScrollElem} ref={messagesEnd} />
        </div>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({
  user: state.activeUser,
  activeBot: state.activeBot,
  searchQuery: state.searchQuery.searchQuery,
})

export default withStyles(styles)(connect(mapStateToProps)(MessagesWrap))
